// Основной сервис для кеширования изображений
import { ImageCacheDB } from './ImageCacheDB.js';
import { CacheManager } from './CacheManager.js';
import { SizeEstimator } from './SizeEstimator.js';
import { DOMCacheHandler } from './DOMCacheHandler.js';
import { AlternativeCacheMethods } from './AlternativeCacheMethods.js';

export class ImageCacheService {
    constructor() {
        this.isOnline = navigator.onLine;
        this.imageCacheDB = new ImageCacheDB();
        this.cacheManager = new CacheManager();
        this.sizeEstimator = new SizeEstimator();
        this.domHandler = new DOMCacheHandler(this.imageCacheDB, this.cacheManager);
        this.alternativeMethods = new AlternativeCacheMethods(this.imageCacheDB, this.cacheManager);
        this.init();
    }

    async init() {
        try {
            await this.imageCacheDB.init();
            await this.cacheManager.init();
        } catch (error) {
            console.log('Image cache initialization failed:', error);
        }
    }

    setOnlineStatus(isOnline) {
        this.isOnline = isOnline;
    }

    // Основные методы кеширования
    async cacheImage(postcard) {
        if (!this.isOnline) return null;
        
        try {
            const imageUrl = postcard.image_url || postcard.preview_78x101_url || postcard.imageUrl;
            if (!imageUrl) return null;

            // Проверяем, есть ли уже в кеше
            const cached = await this.cacheManager.getCachedImage(imageUrl);
            if (cached) {
                return cached;
            }
            
            // Пробуем разные способы загрузки
            let response;
            try {
                // Первая попытка - обычный fetch
                response = await fetch(imageUrl, {
                    mode: 'cors',
                    credentials: 'omit'
                });
            } catch (corsError) {
                // Альтернативный способ - через img элемент
                return await this.alternativeMethods.cacheImageViaCanvas(postcard, imageUrl);
            }
            
            if (!response.ok) {
                return await this.alternativeMethods.cacheImageViaCanvas(postcard, imageUrl);
            }
            
            const imageBlob = await response.blob();
            
            // Кешируем в Cache API
            await this.cacheManager.cacheImage(imageUrl, imageBlob);
            
            // Кешируем в IndexedDB
            await this.imageCacheDB.saveImage(imageUrl, imageBlob, {
                url: imageUrl,
                postcardId: postcard.id,
                originalUrl: imageUrl
            });
            
            return imageBlob;
        } catch (error) {
            console.log('Error caching image:', error);
            return null;
        }
    }

    // Получение кешированного изображения
    async getCachedImage(postcard) {
        try {
            const imageUrl = postcard.image_url || postcard.preview_78x101_url || postcard.imageUrl;
            if (!imageUrl) return null;
            return this.getCachedImageByUrl(imageUrl);
        } catch (error) {
            console.log('Error getting cached image:', error);
            return null;
        }
    }

    async getCachedImageByUrl(imageUrl) {
        try {
            if (!imageUrl) return null;

            // Определяем тип медиа
            const isVideo = imageUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
            const isGif = imageUrl.match(/\.gif$/i);

            // Сначала проверяем Cache API
            let cachedBlob = null;
            if (isVideo) {
                cachedBlob = await this.cacheManager.getCachedVideo(imageUrl);
            } else {
                cachedBlob = await this.cacheManager.getCachedImage(imageUrl);
            }
            
            if (cachedBlob) {
                return URL.createObjectURL(cachedBlob);
            }

            // Затем проверяем IndexedDB
            const cachedData = await this.imageCacheDB.getImage(imageUrl);
            if (cachedData) {
                if (cachedData.blob) {
                    return URL.createObjectURL(cachedData.blob);
                } else if (cachedData.metadata && cachedData.metadata.loaded) {
                    // Возвращаем оригинальный URL, так как медиа уже загружено
                    return imageUrl;
                }
            }

            return null;
        } catch (error) {
            console.log('Error getting cached image by URL:', error);
            return null;
        }
    }

    // Статистика кеша
    async getCacheStats() {
        try {
            const dbStats = await this.imageCacheDB.getCacheStats();
            const cacheSize = await this.cacheManager.getCacheSize();
            
            // Получаем детальную статистику по типам медиа
            const detailedStats = await this.getDetailedCacheStats();
            
            return {
                imagesCount: dbStats.count,
                totalSize: dbStats.totalSize + cacheSize,
                sizeFormatted: this.sizeEstimator.formatSize(dbStats.totalSize + cacheSize),
                ...detailedStats
            };
        } catch (error) {
            console.log('Error getting cache stats:', error);
            return { 
                imagesCount: 0, 
                totalSize: 0, 
                sizeFormatted: '0 B',
                images: { count: 0, size: 0, sizeFormatted: '0 B' },
                videos: { count: 0, size: 0, sizeFormatted: '0 B' }
            };
        }
    }

    async getDetailedCacheStats() {
        try {
            const allImages = await this.imageCacheDB.getAllImages();
            
            const stats = {
                images: { count: 0, size: 0, sizeFormatted: '0 B' },
                videos: { count: 0, size: 0, sizeFormatted: '0 B' }
            };

            allImages.forEach((item, index) => {
                const metadata = item.metadata || {};
                const type = metadata.type || 'image';
                
                // Используем реальный размер из blob или метаданных
                let size = 0;
                if (item.blob && item.blob.size) {
                    size = item.blob.size;
                } else if (metadata.realSize) {
                    size = metadata.realSize;
                } else {
                    size = item.size || 0;
                }
                
                // Определяем тип на основе URL или метаданных
                const url = item.url || metadata.url || '';
                const isVideo = url.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i) || type === 'video';
                
                if (isVideo) {
                    stats.videos.count++;
                    stats.videos.size += size;
                } else {
                    // Все остальные файлы (включая GIF, изображения, превью) считаем изображениями
                    stats.images.count++;
                    stats.images.size += size;
                }
            });

            // Форматируем размеры
            Object.keys(stats).forEach(key => {
                stats[key].sizeFormatted = this.sizeEstimator.formatSize(stats[key].size);
            });

            return stats;
        } catch (error) {
            console.log('Error getting detailed cache stats:', error);
            return {
                images: { count: 0, size: 0, sizeFormatted: '0 B' },
                videos: { count: 0, size: 0, sizeFormatted: '0 B' }
            };
        }
    }

    // Очистка кеша
    async clearCache() {
        try {
            await this.imageCacheDB.clearAll();
            await this.cacheManager.clearAll();
        } catch (error) {
            console.log('Error clearing cache:', error);
        }
    }

    // Делегирование методов DOM обработки
    async cacheLoadedImage(mediaElement, postcard, isPreview = false) {
        return this.domHandler.cacheLoadedImage(mediaElement, postcard, isPreview);
    }

    registerImageForCaching(mediaElement, postcard, isPreview = false) {
        return this.domHandler.registerImageForCaching(mediaElement, postcard, isPreview);
    }

    registerAllImagesOnPage(postcards, isPreview = false) {
        return this.domHandler.registerAllImagesOnPage(postcards, isPreview);
    }

    registerImageBySelector(selector, postcard) {
        return this.domHandler.registerImageBySelector(selector, postcard);
    }
}
